import {inject, Service} from "@angular/core";
import type {IJsonObject} from "dcerialize";
import type {ISerializableType} from "dcerialize/dist/types";
import type {Observable} from "rxjs";
import type {NonEmptyTuple} from "type-fest";
import type {NonEmptyArray} from "../../../helpers/ArrayLengthHelpers";
import type {BactoforceLocation} from "../../../models/bactoforce/BactoforceLocation";
import {IdOnlyBactoforceLocation} from "../../../models/bactoforce/BactoforceLocation";
import {BasicFile} from "../../../models/base/BasicFile";
import type {Existing, IdOf, New, NewOrExisting} from "../../../models/base/IdentifiableModel";
import {isNew} from "../../../models/base/IdentifiableModel";
import type {IListParameters, IPaginatedListParameters} from "../../../models/base/ListParameters";
import type {Paginated} from "../../../models/base/Paginated";
import type {CustomerDepartment} from "../../../models/customers/CustomerDepartment";
import type {CustomerLocation} from "../../../models/customers/CustomerLocation";
import type {CustomerOrganization} from "../../../models/customers/CustomerOrganization";
import type {DownloadResponse} from "../../../models/DownloadResponse";
import type {LanguageCode} from "../../../models/i18n/Language";
import {type QueryParameters, RequestBuilder, type RequestBuilderWithoutBody} from "../../../models/RequestBuilder";
import type {HttpResponseWithBody, ResponseHandler} from "../../../models/ResponseProcessor";
import type {PermissionId} from "../../../models/users/Permission";
import type {
  AnyBactoforceUser,
  AnyUser,
  AnyWritableUser,
  BaseCommonUser,
  BaseUser,
  TypedIdOnlyUser,
} from "../../../models/users/User";
import {
  AdminUser,
  BactoforceUser,
  ContactableUser,
  CreatableCustomerUser,
  CustomerUser,
  ThirdPartyUser,
  UserAccountType,
  WritableAdminUser,
  WritableBactoforceUser,
  WritableCustomerUser,
  WritableThirdPartyUser,
} from "../../../models/users/User";
import {
  BactoforceLocationUserAssignment,
  CustomerUserLocationAssignment,
  ThirdPartyUserCustomerAssignment,
  WritableThirdPartyUserCustomerAssignment,
} from "../../../models/users/UserAssignment";
import {CredentialsStorage} from "../../credentials-storage";
import {BaseApi} from "../base-api";

@Service()
export class UsersApi extends BaseApi {
  private readonly credentials = inject(CredentialsStorage).nonExpiredCredentials;

  public constructor() {
    super("users");
  }

  private getAccountTypeUrl(type: UserAccountType): string {
    switch (type) {
      case UserAccountType.Admin:
        return "admins";
      case UserAccountType.Bactoforce:
        return "bactoforce";
      case UserAccountType.Customer:
        return "customers";
      case UserAccountType.ThirdParty:
        return "third-party";
    }
  }

  private getAccountTypeAssignmentUrl(type: UserAccountType): string {
    switch (type) {
      case UserAccountType.Admin:
      case UserAccountType.Bactoforce:
        return "bactoforce-location-assignments";
      case UserAccountType.Customer:
      case UserAccountType.ThirdParty:
        return "customer-location-assignments";
    }
  }

  private customerAssignmentQueryToQueryParameters(assignmentQuery: {
    readonly organization?: IdOf<CustomerOrganization>,
    readonly location?: IdOf<CustomerLocation>,
    readonly department?: IdOf<CustomerDepartment>,
  }): QueryParameters {
    const params: QueryParameters = {};
    if (assignmentQuery.organization !== undefined)
      params["organization-id"] = assignmentQuery.organization;
    if (assignmentQuery.location !== undefined)
      params["location-id"] = assignmentQuery.location;
    if (assignmentQuery.department !== undefined)
      params["department-id"] = assignmentQuery.department;
    return params;
  }

  // private listRequestOfType(type: UserAccountType.Admin | UserAccountType.ThirdParty): RequestBuilderWithoutBody<IJsonObject>;
  // private listRequestOfType(
  //   type: UserAccountType.Bactoforce,
  //   assignedTo?: { readonly location?: IdOf<BactoforceLocation> }): RequestBuilderWithoutBody<IJsonObject>;
  // private listRequestOfType(
  //   type: UserAccountType.Customer,
  //   assignedTo?: {
  //     readonly organization?: IdOf<CustomerOrganization>,
  //     readonly location?: IdOf<CustomerLocation>,
  //     readonly department?: IdOf<CustomerDepartment>,
  //   }): RequestBuilderWithoutBody<IJsonObject>;
  private listRequestOfType(
    type: UserAccountType,
    assignedTo?: {
      readonly organization?: IdOf<CustomerOrganization>,
      // noinspection TypeScriptDuplicateUnionOrIntersectionType
      readonly location?: IdOf<CustomerLocation> | IdOf<BactoforceLocation>, // eslint-disable-line @typescript-eslint/no-duplicate-type-constituents
      readonly department?: IdOf<CustomerDepartment>,
    }): RequestBuilderWithoutBody<IJsonObject> {
    let request = RequestBuilder.get(this.getAccountTypeUrl(type)).authenticate().expectJsonObject();
    if (assignedTo !== undefined) {
      switch (type) {
        case UserAccountType.Bactoforce:
          if (assignedTo.location !== undefined)
            request = request.withQueryParameter("location-id", assignedTo.location);
          break;
        case UserAccountType.Customer:
          request = request.withQueryParameters(this.customerAssignmentQueryToQueryParameters(assignedTo));
          break;
      }
    }
    return request;
  }

  public listOfType(
    type: UserAccountType.Admin,
    params?: IPaginatedListParameters<Existing<AdminUser>>): Observable<Paginated<Existing<AdminUser>>>;
  public listOfType(
    type: UserAccountType.Bactoforce,
    params?: IPaginatedListParameters<Existing<BactoforceUser>>,
    assignedTo?: { readonly location?: IdOf<BactoforceLocation> }): Observable<Paginated<Existing<BactoforceUser>>>;
  public listOfType(
    type: UserAccountType.Customer,
    params?: IPaginatedListParameters<Existing<CustomerUser>>,
    assignedTo?: {
      readonly organization?: IdOf<CustomerOrganization>,
      readonly location?: IdOf<CustomerLocation>,
      readonly department?: IdOf<CustomerDepartment>,
    }): Observable<Paginated<Existing<CustomerUser>>>;
  public listOfType(
    type: UserAccountType.ThirdParty,
    params: IPaginatedListParameters<Existing<ThirdPartyUser>>): Observable<Paginated<Existing<ThirdPartyUser>>>;
  public listOfType(
    type: UserAccountType,
    params: IPaginatedListParameters<Existing<AdminUser>>
      | IPaginatedListParameters<Existing<BactoforceUser>>
      | IPaginatedListParameters<Existing<CustomerUser>>
      | IPaginatedListParameters<Existing<ThirdPartyUser>> = {},
    assignedTo?: {
      readonly organization?: IdOf<CustomerOrganization>,
      // noinspection TypeScriptDuplicateUnionOrIntersectionType
      readonly location?: IdOf<CustomerLocation> | IdOf<BactoforceLocation>, // eslint-disable-line @typescript-eslint/no-duplicate-type-constituents
      readonly department?: IdOf<CustomerDepartment>,
    },
  ): Observable<Paginated<Existing<AdminUser>>
    | Paginated<Existing<BactoforceUser>>
    | Paginated<Existing<CustomerUser>>
    | Paginated<Existing<ThirdPartyUser>>> {
    const request = this.listRequestOfType(type, assignedTo)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      .withPaginatedListParameters(params as IPaginatedListParameters<Existing<AnyUser>>);
    const response = this.execute(request).notNull();
    switch (type) {
      case UserAccountType.Admin:
        return response.decodePaginatedExistingOf(AdminUser);
      case UserAccountType.Bactoforce:
        return response.decodePaginatedExistingOf(BactoforceUser);
      case UserAccountType.Customer:
        return response.decodePaginatedExistingOf(CustomerUser);
      case UserAccountType.ThirdParty:
        return response.decodePaginatedExistingOf(ThirdPartyUser);
    }
  }

  public listAllOfType(type: UserAccountType.Admin,
                       params?: IListParameters<Existing<AdminUser>>): Observable<Existing<AdminUser>[]>;
  public listAllOfType(
    type: UserAccountType.Bactoforce,
    params?: IListParameters<Existing<BactoforceUser>>,
    assignedTo?: { readonly location?: IdOf<BactoforceLocation> },
  ): Observable<Existing<BactoforceUser>[]>;
  public listAllOfType(
    type: UserAccountType.Customer,
    params?: IListParameters<Existing<CustomerUser>>,
    assignedTo?: {
      readonly organization?: IdOf<CustomerOrganization>,
      readonly location?: IdOf<CustomerLocation>,
      readonly department?: IdOf<CustomerDepartment>,
    },
  ): Observable<Existing<CustomerUser>[]>;
  public listAllOfType(type: UserAccountType.ThirdParty,
                       params?: IListParameters<Existing<ThirdPartyUser>>): Observable<Existing<ThirdPartyUser>[]>;
  public listAllOfType(
    type: UserAccountType,
    params: IListParameters<Existing<AdminUser>>
      | IListParameters<Existing<BactoforceUser>>
      | IListParameters<Existing<CustomerUser>>
      | IListParameters<Existing<ThirdPartyUser>> = {},
    assignedTo?: {
      readonly organization?: IdOf<CustomerOrganization>,
      // noinspection TypeScriptDuplicateUnionOrIntersectionType
      readonly location?: IdOf<CustomerLocation> | IdOf<BactoforceLocation>, // eslint-disable-line @typescript-eslint/no-duplicate-type-constituents
      readonly department?: IdOf<CustomerDepartment>,
    },
  ): Observable<Existing<AnyUser>[]> {
    const request = this.listRequestOfType(type, assignedTo)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      .withListParameters(params as IListParameters<Existing<AnyUser>>);
    switch (type) {
      case UserAccountType.Admin:
        return this.depaginateRequestOfExisting(request, AdminUser);
      case UserAccountType.Bactoforce:
        return this.depaginateRequestOfExisting(request, BactoforceUser);
      case UserAccountType.Customer:
        return this.depaginateRequestOfExisting(request, CustomerUser);
      case UserAccountType.ThirdParty:
        return this.depaginateRequestOfExisting(request, ThirdPartyUser);
    }
  }

  public exportCustomers(params: IListParameters<Existing<CustomerUser>> = {},
                         assignedTo?: {
                           readonly organization?: IdOf<CustomerOrganization>,
                           readonly location?: IdOf<CustomerLocation>,
                           readonly department?: IdOf<CustomerDepartment>,
                         }): Observable<DownloadResponse<Blob>> {
    let request = RequestBuilder.get("customers", "export").authenticate().withListParameters(params).expectBlob();
    if (assignedTo) request = request.withQueryParameters(this.customerAssignmentQueryToQueryParameters(assignedTo));
    return this.execute(request).notNull().download();
  }

  private decodeExistingOfType(type: UserAccountType,
                               // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
                               response: ResponseHandler<HttpResponseWithBody<IJsonObject>>): Observable<Existing<AnyUser>> {
    switch (type) {
      case UserAccountType.Admin:
        return response.decodeExistingJsonObjectOf(AdminUser);
      case UserAccountType.Bactoforce:
        return response.decodeExistingJsonObjectOf(BactoforceUser);
      case UserAccountType.Customer:
        return response.decodeExistingJsonObjectOf(CustomerUser);
      case UserAccountType.ThirdParty:
        return response.decodeExistingJsonObjectOf(ThirdPartyUser);
    }
  }

  public getOfType(type: UserAccountType.Admin, id: IdOf<AdminUser>): Observable<Existing<AdminUser>>;
  public getOfType(type: UserAccountType.Bactoforce, id: IdOf<BactoforceUser>): Observable<Existing<BactoforceUser>>;
  public getOfType(type: UserAccountType.Customer, id: IdOf<CustomerUser>): Observable<Existing<CustomerUser>>;
  public getOfType(type: UserAccountType.ThirdParty, id: IdOf<ThirdPartyUser>): Observable<Existing<ThirdPartyUser>>;
  public getOfType(type: UserAccountType, id: IdOf<AnyUser>): Observable<Existing<AnyUser>> {
    const request = RequestBuilder.get(this.getAccountTypeUrl(type), id).authenticate().expectJsonObject();
    const response = this.execute(request).notNull();
    return this.decodeExistingOfType(type, response);
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  private _create(user: Readonly<New<AnyWritableUser>>,
                  type: ISerializableType<BaseUser<UserAccountType>>): Observable<Existing<AnyUser>> {
    const request = RequestBuilder.post<New<BaseCommonUser>>(user, this.getAccountTypeUrl(user.type))
      .serializeAsJsonOf(type).authenticate().expectJsonObject();
    return this.decodeExistingOfType(user.type, this.execute(request).notNull());
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public create(user: Readonly<New<WritableAdminUser>>): Observable<Existing<AdminUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public create(user: Readonly<New<WritableBactoforceUser>>): Observable<Existing<BactoforceUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public create(user: Readonly<New<CreatableCustomerUser>>): Observable<Existing<CustomerUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public create(user: Readonly<New<WritableThirdPartyUser>>): Observable<Existing<ThirdPartyUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public create(user: Readonly<New<AnyWritableUser>>): Observable<Existing<AnyUser>> {
    switch (user.type) {
      case UserAccountType.Admin:
        return this._create(user, WritableAdminUser);
      case UserAccountType.Bactoforce:
        return this._create(user, WritableBactoforceUser);
      case UserAccountType.Customer:
        return this._create(user, CreatableCustomerUser);
      case UserAccountType.ThirdParty:
        return this._create(user, WritableThirdPartyUser);
    }
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  private _update(user: Readonly<Existing<AnyWritableUser>>,
                  type: ISerializableType<BaseUser<UserAccountType>>): Observable<Existing<AnyUser>> {
    const request = RequestBuilder.put<Existing<BaseCommonUser>>(user, this.getAccountTypeUrl(user.type), user.id)
      .serializeAsJsonOf(type).authenticate().expectJsonObject();
    return this.decodeExistingOfType(user.type, this.execute(request).notNull());
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public update(user: Readonly<Existing<WritableAdminUser>>): Observable<Existing<AdminUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public update(user: Readonly<Existing<WritableBactoforceUser>>): Observable<Existing<BactoforceUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public update(user: Readonly<Existing<WritableCustomerUser>>): Observable<Existing<CustomerUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public update(user: Readonly<Existing<WritableThirdPartyUser>>): Observable<Existing<ThirdPartyUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public update(user: Readonly<Existing<AnyWritableUser>>): Observable<Existing<AnyUser>> {
    switch (user.type) {
      case UserAccountType.Admin:
        return this._update(user, WritableAdminUser);
      case UserAccountType.Bactoforce:
        return this._update(user, WritableBactoforceUser);
      case UserAccountType.Customer:
        return this._update(user, WritableCustomerUser);
      case UserAccountType.ThirdParty:
        return this._update(user, WritableThirdPartyUser);
    }
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public save(user: NewOrExisting<Readonly<WritableAdminUser>>): Observable<Existing<AdminUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public save(user: NewOrExisting<Readonly<WritableBactoforceUser>>): Observable<Existing<BactoforceUser>>;
  public save(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    user: New<Readonly<CreatableCustomerUser>> | Existing<Readonly<WritableCustomerUser>>,
  ): Observable<Existing<CustomerUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public save(user: NewOrExisting<Readonly<WritableThirdPartyUser>>): Observable<Existing<ThirdPartyUser>>;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public save(user: NewOrExisting<Readonly<AnyWritableUser>>): Observable<Existing<AnyUser>> {
    switch (user.type) {
      case UserAccountType.Admin:
        return isNew(user) ? this.create(user) : this.update(user);
      case UserAccountType.Bactoforce:
        return isNew(user) ? this.create(user) : this.update(user);
      case UserAccountType.Customer:
        return isNew(user) ? this._create(user, CreatableCustomerUser) : this.update(user);
      case UserAccountType.ThirdParty:
        return isNew(user) ? this.create(user) : this.update(user);
    }
  }

  public softDelete(model: Readonly<Existing<TypedIdOnlyUser>>): Observable<never> {
    const request = RequestBuilder.put(null, this.getAccountTypeUrl(model.type), model.id, "delete").authenticate();
    return this.execute(request).noContent();
  }

  public restore(model: Readonly<Existing<TypedIdOnlyUser>>): Observable<never> {
    const request = RequestBuilder.put(null, this.getAccountTypeUrl(model.type), model.id, "restore").authenticate();
    return this.execute(request).noContent();
  }

  public getMe(): Observable<Existing<AnyUser>> {
    const response = this.execute(RequestBuilder.get("me").authenticate().expectJsonObject()).notNull();
    const currentUserType = this.credentials()?.accountType;
    if (currentUserType === undefined) throw new Error("Not logged in!");
    return this.decodeExistingOfType(currentUserType, response);
  }

  public getContactableMe(): Observable<Existing<ContactableUser>> {
    return this.execute(RequestBuilder.get("me", "simple").authenticate().expectJsonObject())
      .notNull().decodeExistingJsonObjectOf(ContactableUser);
  }

  public getMyPermissions(): Observable<PermissionId[]> {
    const request = RequestBuilder.get("me", "permissions").authenticate().expectJsonArray<PermissionId[]>();
    return this.execute(request).notNull().readAsIs();
  }

  public changeMyLanguage(languageCode: LanguageCode): Observable<never> {
    return this.execute(RequestBuilder.put({languageCode}, "me", "language").authenticate()).noContent();
  }

  private bactoforceLocationAssignmentsRequest<TUser extends AnyBactoforceUser>(
    model: Readonly<Existing<TUser>>,
    params: IPaginatedListParameters<BactoforceLocationUserAssignment>,
  ): RequestBuilderWithoutBody<IJsonObject> {
    return RequestBuilder.get(this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type))
      .authenticate().withPaginatedListParameters(params).expectJsonObject();
  }

  public listBactoforceLocationAssignments(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<AnyBactoforceUser>>,
    params: IPaginatedListParameters<BactoforceLocationUserAssignment> = {},
  ): Observable<Paginated<BactoforceLocationUserAssignment>> {
    return this.execute(this.bactoforceLocationAssignmentsRequest(model, params))
      .notNull().decodePaginatedOf(BactoforceLocationUserAssignment);
  }

  public listAllBactoforceLocationAssignments(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<AnyBactoforceUser>>,
    params: IListParameters<BactoforceLocationUserAssignment> = {},
  ): Observable<BactoforceLocationUserAssignment[]> {
    return this.depaginateRequest(this.bactoforceLocationAssignmentsRequest(model, params), BactoforceLocationUserAssignment);
  }

  public assignBactoforceLocation(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<AnyBactoforceUser>>,
    bfLocationId: IdOf<BactoforceLocation>,
  ): Observable<BactoforceLocationUserAssignment> {
    const request = RequestBuilder.put(null,
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), bfLocationId)
      .authenticate().expectJsonObject();
    return this.execute(request).notNull().decodeJsonObjectOf(BactoforceLocationUserAssignment);
  }

  public assignBactoforceLocations(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<AnyBactoforceUser>>,
    bfLocations: Readonly<NonEmptyArray<Readonly<IdOnlyBactoforceLocation>>>
      | Readonly<NonEmptyTuple<Readonly<IdOnlyBactoforceLocation>>>,
  ): Observable<readonly BactoforceLocationUserAssignment[]> {
    const request = RequestBuilder.put(bfLocations,
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), "assign")
      .serializeAsJsonOf(IdOnlyBactoforceLocation).authenticate().expectJsonArray();
    return this.execute(request).notNull().decodeJsonArrayOf(BactoforceLocationUserAssignment);
  }

  public unassignBactoforceLocation(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<AnyBactoforceUser>>,
    bfLocationId: IdOf<BactoforceLocation>,
  ): Observable<never> {
    const request = RequestBuilder.delete(
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), bfLocationId)
      .authenticate().expectNoContent();
    return this.execute(request).noContent();
  }

  public unassignBactoforceLocations(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<AnyBactoforceUser>>,
    bfLocations: Readonly<NonEmptyArray<Readonly<IdOnlyBactoforceLocation>>>
      | Readonly<NonEmptyTuple<Readonly<IdOnlyBactoforceLocation>>>,
  ): Observable<never> {
    const request = RequestBuilder.put(bfLocations,
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), "unassign")
      .serializeAsJsonOf(IdOnlyBactoforceLocation).authenticate().expectNoContent();
    return this.execute(request).noContent();
  }

  private listCustomerAssignmentsRequest(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableCustomerUser>>,
    params: IPaginatedListParameters<Existing<CustomerUserLocationAssignment>>,
  ): RequestBuilderWithoutBody<IJsonObject> {
    return RequestBuilder.get(this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type))
      .authenticate().withPaginatedListParameters(params).expectJsonObject();
  }

  public listCustomerAssignments(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableCustomerUser>>,
    params: IPaginatedListParameters<Existing<CustomerUserLocationAssignment>> = {},
  ): Observable<Paginated<Existing<CustomerUserLocationAssignment>>> {
    return this.execute(this.listCustomerAssignmentsRequest(model, params))
      .notNull().decodePaginatedExistingOf(CustomerUserLocationAssignment);
  }

  public listAllCustomerLocationAssignments(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableCustomerUser>>,
    params: IListParameters<Existing<CustomerUserLocationAssignment>> = {},
  ): Observable<Existing<CustomerUserLocationAssignment>[]> {
    return this.depaginateRequestOfExisting(this.listCustomerAssignmentsRequest(model, params),
      CustomerUserLocationAssignment);
  }

  public assignCustomerLocation(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableCustomerUser>>,
    locationId: IdOf<CustomerLocation>,
    departmentId?: IdOf<CustomerDepartment>,
  ): Observable<Existing<CustomerUserLocationAssignment>> {
    const request = RequestBuilder.post(null,
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), locationId)
      .authenticate()
      .withQueryParameters(departmentId !== undefined ? {"customer-department-id": departmentId} : {})
      .expectJsonObject();
    return this.execute(request).notNull().decodeExistingJsonObjectOf(CustomerUserLocationAssignment);
  }

  public unassignCustomerLocation(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableCustomerUser>>,
    assignmentId: IdOf<CustomerUserLocationAssignment>,
  ): Observable<never> {
    const request = RequestBuilder.delete(
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), assignmentId)
      .authenticate().expectNoContent();
    return this.execute(request).noContent();
  }

  private listThirdPartyUserCustomerAssignmentsRequest(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableThirdPartyUser>>,
    params: IPaginatedListParameters<ThirdPartyUserCustomerAssignment>,
  ): RequestBuilderWithoutBody<IJsonObject> {
    return RequestBuilder.get(this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type))
      .authenticate().withPaginatedListParameters(params).expectJsonObject();
  }

  public listThirdPartyUserCustomerAssignments(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableThirdPartyUser>>,
    params: IPaginatedListParameters<ThirdPartyUserCustomerAssignment> = {},
  ): Observable<Paginated<ThirdPartyUserCustomerAssignment>> {
    return this.execute(this.listThirdPartyUserCustomerAssignmentsRequest(model, params))
      .notNull().decodePaginatedOf(ThirdPartyUserCustomerAssignment);
  }

  public listAllThirdPartyUserCustomerLocationAssignments(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableThirdPartyUser>>,
    params: IListParameters<ThirdPartyUserCustomerAssignment> = {},
  ): Observable<ThirdPartyUserCustomerAssignment[]> {
    return this.depaginateRequest(this.listThirdPartyUserCustomerAssignmentsRequest(model, params),
      ThirdPartyUserCustomerAssignment);
  }

  public assignThirdPartyUserToCustomerLocation(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableThirdPartyUser>>,
    locationId: IdOf<CustomerLocation>,
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    assignment: Readonly<WritableThirdPartyUserCustomerAssignment>,
  ): Observable<ThirdPartyUserCustomerAssignment> {
    const request = RequestBuilder.post(assignment,
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), locationId)
      .serializeAsJsonOf(WritableThirdPartyUserCustomerAssignment).authenticate().expectJsonObject();
    return this.execute(request).notNull().decodeJsonObjectOf(ThirdPartyUserCustomerAssignment);
  }

  public unassignThirdPartyUserFromCustomerLocation(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    model: Readonly<Existing<WritableThirdPartyUser>>,
    locationId: IdOf<CustomerLocation>,
  ): Observable<never> {
    const request = RequestBuilder.delete(
      this.getAccountTypeUrl(model.type), model.id, this.getAccountTypeAssignmentUrl(model.type), locationId)
      .authenticate();
    return this.execute(request).noContent();
  }

  public uploadSignature(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    user: Readonly<Existing<AnyBactoforceUser>>,
    file: Readonly<BasicFile>,
  ): Observable<never> {
    const request = RequestBuilder.put(file, this.getAccountTypeUrl(user.type), user.id, "signature")
      .serializeAsJsonOf(BasicFile).authenticate().expectNoContent();
    return this.execute(request).noContent();
  }

  public downloadSignature(
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    user: Readonly<Existing<AnyBactoforceUser>>,
  ): Observable<DownloadResponse<Blob>> {
    const request = RequestBuilder.get(this.getAccountTypeUrl(user.type), user.id, "signature")
      .authenticate().expectBlob();
    return this.execute(request).notNull().download();
  }
}
